import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useEimParam } from '~/contexts/UrlParamsContext';
import { useUpdateVehicleOptions, useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { isValidRules } from '~/domain/configurationRule';
import { validateEim } from '~/domain/features';
import { isValidFeature } from '~/domain/features';
import { cleanupEim } from '~/domain/features/eimParser';
import { IConfigurationOptionGroup } from '~/domain/IConfigurationOptionGroup';
import { IConfigurationRule } from '~/domain/IConfigurationRule';
import { useProductConfigurationQuery } from '~/hooks/useProductConfigurationQuery';
import { useRequestUrl } from '~/hooks/useRequestUrl';
import { useSearchEimNavigate } from '~/hooks/useSearchEimUrl';

const emptyArray: unknown = [];
export const RequestUrlTracker: React.FC = () => {
  const eim = useEimParam();
  const location = useLocation();
  const requestUrl = useRequestUrl();
  const navigateToSearchEim = useSearchEimNavigate()
  const vehicleOptions = useVehicleOptions();
  const updateVehicleOptions = useUpdateVehicleOptions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const featuresString = searchParams.get('f') || '';
  const {
    isSuccess,
    data: {
      packSets = emptyArray as IConfigurationOptionGroup[],
      eimPackSets = emptyArray as IConfigurationOptionGroup[],
      rules = emptyArray as IConfigurationRule[],
      client,
    } = {},
  } = useProductConfigurationQuery();

  // redirects to search iem page when url has invalid eim
  useEffect(() => {
    updateVehicleOptions((prev) => {
      if (client === 'subaru') {
        return prev;
      }
      const cleanEim = cleanupEim(eim);
      const loadFeatures = featuresString.split(',');

      // this if is for when loading an url for the first time
      // we must validate eim and features typed in the url
      if (!prev.eim && isSuccess) {
        const validFeatures = !featuresString || (isValidFeature(loadFeatures, packSets) && isValidRules(loadFeatures, rules));
        if (!validFeatures) {
          navigateToSearchEim();
          return prev;
        }
        const [features] = validateEim(eimPackSets, packSets, rules, eim);
        if (features) {
          return { eim: cleanEim, features: featuresString ? loadFeatures : features };
        }
        navigateToSearchEim();
        return prev;
      }

      // this if is for when users type an eim or click on load configuration within the app
      // we do not allow users to load an invalid configuration after the app is loaded
      // thus there is no need to validate that here.
      if (featuresString && !!prev.eim) {
        return {
          ...prev,
          eim: cleanEim,
          features: loadFeatures,
        }
      }
      return prev;
    });
  }, [eim, client, eimPackSets, featuresString, isSuccess, navigateToSearchEim, packSets, rules, updateVehicleOptions]);

  useEffect(() => {
    if (featuresString && featuresString === vehicleOptions.features.join(',')) {
      // ingest query params, and remove it from url
      setSearchParams((prev) => {
        return {
          ...Object.fromEntries(prev),
          f: [],
        }
      }, { replace: true, state: location.state });
    }
  }, [vehicleOptions, featuresString, location.state, setSearchParams]);

  // update url when a valid eim is typed or config option selected
  useEffect(() => {
    const { eim: contextEim } = vehicleOptions;
    if (contextEim && contextEim !== eim) {
      const url = requestUrl({ eim: contextEim })
      navigate(url, { replace: true });
    }
  }, [vehicleOptions, eim, navigate, requestUrl]);

  return null;
}
